import { motion } from "framer-motion";
import { Mic, FileText, DollarSign, BookOpen } from "lucide-react";

export const MasterclassSection = () => {
  return (
    <section data-testid="masterclass-section" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="font-inter text-xs font-bold tracking-[0.3em] uppercase text-gold">Masterclass SOP Builder</span>
          <h2 className="font-inter text-4xl md:text-5xl font-black text-white mt-4">
            Capture Wisdom, <span className="text-gold">Instantly.</span>
          </h2>
          <p className="text-base md:text-lg text-slate-400 mt-4 max-w-lg mx-auto">
            Your best employees, cloned. Record any task and Argus converts it into a professional, cost-analyzed manual in seconds.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <motion.div className="glass-card rounded-xl p-8 border border-gold/10" data-testid="masterclass-card-0"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-5">
              <Mic className="w-6 h-6 text-gold" />
            </div>
            <h3 className="font-inter text-lg font-bold text-white mb-2">Voice-to-SOP</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Record any task — prep, cleaning, closing. Argus converts it into a professional, cost-analyzed PDF manual in seconds.
            </p>
          </motion.div>

          <motion.div className="glass-card rounded-xl p-8 border border-gold/10" data-testid="masterclass-card-1"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <div className="w-12 h-12 rounded-lg bg-blue-400/10 flex items-center justify-center mb-5">
              <BookOpen className="w-6 h-6 text-blue-400" />
            </div>
            <h3 className="font-inter text-lg font-bold text-white mb-2">Dynamic Training</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              The moment a recipe is recorded, it's live. New hires ask the AI for step-by-step guidance using the chef's own recorded logic.
            </p>
          </motion.div>

          <motion.div className="glass-card rounded-xl p-8 border border-gold/10" data-testid="masterclass-card-2"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
            <div className="w-12 h-12 rounded-lg bg-lime/10 flex items-center justify-center mb-5">
              <DollarSign className="w-6 h-6 text-lime" />
            </div>
            <h3 className="font-inter text-lg font-bold text-white mb-2">Know Your Real Margins</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              AI calculates cost per plate based on your entered ingredient costs. Always know exactly where your margins stand — no guesswork, no manual spreadsheets.
            </p>
          </motion.div>
        </div>

        {/* Voice-to-SOP visual */}
        <motion.div className="max-w-3xl mx-auto glass-card rounded-xl p-6 border border-gold/20"
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
          <div className="flex items-center gap-8">
            {/* Voice wave */}
            <div className="flex-1 flex items-center gap-1 justify-center h-16">
              {[...Array(16)].map((_, i) => (
                <motion.div key={i} className="w-1 bg-gold rounded-full"
                  animate={{ height: [6, Math.random() * 40 + 8, 6] }}
                  transition={{ duration: 0.6 + Math.random() * 0.4, repeat: Infinity, delay: i * 0.04 }} />
              ))}
            </div>
            {/* Arrow */}
            <div className="text-gold text-2xl font-bold shrink-0">&rarr;</div>
            {/* PDF document */}
            <div className="flex-1 bg-indigo-700/40 rounded-lg p-4 border border-gold/10">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-gold" />
                <span className="text-xs font-inter font-bold text-gold">AI-Generated SOP</span>
              </div>
              <div className="space-y-1.5">
                <div className="h-2 bg-slate-600/40 rounded w-full" />
                <div className="h-2 bg-slate-600/40 rounded w-4/5" />
                <div className="h-2 bg-lime/20 rounded w-3/5" />
                <div className="h-2 bg-slate-600/40 rounded w-full" />
              </div>
              <div className="mt-3 flex items-center gap-2">
                <DollarSign className="w-3 h-3 text-lime" />
                <span className="text-xs text-lime font-mono">Cost: $4.72/plate</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
